<script lang="ts">
  import { SecurityFindingSeverityLevel } from "$lib/models/base";
  import { arc, pie, scaleOrdinal, select } from "d3";
  import { ChartLine } from "lucide-svelte";
  import { onMount } from "svelte";
  import { type SecurityFinding } from "$lib/stores/orgSecurityFindings";
  import { toTitleCase } from "$lib/utils/text";
  import { mapSeverityScoreToLevel } from "$lib/utils/securityFindings";
  import { countBy, reduce, map } from "lodash";
  import { formatNumberWithCommas } from "$lib/utils/number";
  const { findings } = $props<{ findings: SecurityFinding[] }>();
  const severityValues = Object.values(SecurityFindingSeverityLevel);

  let severityCounts = $derived.by(() => {
    const startTime = performance.now();
    const valueToSeverity = map(findings, (finding) => finding.severityLevel);
    const findingCounts = countBy(valueToSeverity, (severity) => severity);
    const counts = reduce(
      severityValues,
      (acc, severity) => {
        acc[severity] = findingCounts[severity] || 0;
        return acc;
      },
      {} as Record<SecurityFindingSeverityLevel, number>,
    );
    const endTime = performance.now();
    console.log("[SeverityChartCard] ** Time taken **", endTime - startTime);
    console.log("[SeverityChartCard] ** Counts **", counts);
    return counts;
  });

  $effect(() => {
    console.log("[SeverityChartCard] ** Computing stats **", findings.length);
    drawChart();
  });

  onMount(() => {
    const resize = () => {
      select(chartContainer).select("svg").remove();
      drawChart();
    };

    window.addEventListener("resize", resize);

    return () => {
      // Cleanup
      select(chartContainer).select("svg").remove();
      window.removeEventListener("resize", resize);
    };
  });

  let chartContainer: HTMLDivElement;
  interface DataPoint {
    label: string;
    value: number;
  }

  // Define color scale for both chart and legend
  const color = scaleOrdinal<string>().domain(Object.values(SecurityFindingSeverityLevel)).range([
    "#b22d3f", // red (for CRITICAL)
    "#f06c6c", // orange (for HIGH)
    "#ffbe0f", // yellow (for MEDIUM)
    "#65d5da", // blue (for LOW)
  ]);

  const drawChart = () => {
    if (!chartContainer || !severityCounts) return;
    select(chartContainer).select("svg").remove();

    const data = Object.entries(severityCounts).map(([severity, count]) => ({
      label: severity,
      value: count,
    }));

    const margin = { top: 5, right: 5, bottom: 5, left: 5 };
    const width = chartContainer.clientWidth - margin.left - margin.right;
    const height = chartContainer.clientHeight - margin.top - margin.bottom;

    const radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 2.5;

    const svg = select(chartContainer)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${width / 2},${chartContainer.clientHeight / 2})`);

    const pieGenerator = pie<DataPoint>().value((d) => d.value);
    //   .sort(null);

    const arcGenerator = arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);

    const paths = svg
      .selectAll("path")
      .data(pieGenerator(data))
      .enter()
      .append("path")
      .attr("d", arcGenerator)
      .attr("fill", (d) => color(d.data.label))
      .attr("stroke", "white")
      .style("stroke-width", "2px");
  };
</script>

<div class="col-span-1 row-span-1 bg-white rounded-lg shadow flex h-full">
  <div class="flex-1 flex flex-col">
    <div class="px-4 py-5 sm:px-6">
      <div class="flex gap-2 items-center">
        <span class=""><ChartLine /></span>
        <h2 class="font-bold text-base">Severity</h2>
      </div>
    </div>

    <div class="flex flex-1">
      <div class="px-4 py-5 sm:p-6 flex gap-4 text-gray-500 flex-col shrink-0 min-w-[200px]">
        <div class="mt-auto">
          <ul class="space-y-2">
            {#each Object.entries(severityCounts).filter(([_, count]) => count > 0) as [severity, count]}
              {@const total = Object.values(severityCounts).reduce((sum, c) => sum + c, 0)}
              {@const percentage = total > 0 ? Math.round((count / total) * 100) : 0}
              <li class="flex items-center gap-3">
                <span class="w-2 h-2 rounded-full shrink-0" style="background-color: {color(severity)}"></span>
                <div class="flex items-center gap-1.5 min-w-0">
                  <span class="text-sm font-medium whitespace-nowrap">{formatNumberWithCommas(count)}</span>
                  <span class="text-sm text-gray-600 whitespace-nowrap">{toTitleCase(severity.toLowerCase())}</span>
                  <span class="text-sm text-gray-600 whitespace-nowrap">({percentage}%)</span>
                </div>
              </li>
            {/each}
          </ul>
          <div class="mt-4 pt-4 border-t border-gray-200">
            <div class="flex items-center gap-1">
              <span class="text-sm font-bold"
                >{formatNumberWithCommas(Object.values(severityCounts).reduce((sum, count) => sum + count, 0))}</span
              >
              <span class="text-sm text-gray-600">Issues Detected</span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-1 flex flex-col relative">
        <div class="flex-grow" id="chart" bind:this={chartContainer}></div>
      </div>
    </div>
  </div>
</div>
